import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import type { Calendar } from 'commoncal-shared';

export type CalendarWithRole = Calendar & {
  role?: string | null;
  is_member?: boolean;
  is_subscribed?: boolean;
};

export function useCalendars() {
  return useQuery({
    queryKey: ['calendars'],
    queryFn: () => api.calendars.list(),
    select: data => data.calendars,
  });
}

export function useCreateCalendar() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: api.calendars.create,
    onSuccess: ({ calendar }) => {
      qc.setQueryData<{ calendars: CalendarWithRole[] }>(
        ['calendars'],
        old => {
          if (!old) return old;
          return {
            calendars: [
              ...old.calendars,
              {
                ...calendar,
                role: 'owner',
                is_member: true,
                is_subscribed: false,
              },
            ],
          };
        }
      );
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['calendars'] });
    },
  });
}

export function useUpdateCalendar() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: Partial<{
      name: string;
      color: string;
      description: string | null;
      timezone: string;
      warn_on_conflicts: boolean;
      is_kocal: boolean;
    }> & { id: string }) => api.calendars.update(id, body),
    onMutate: async ({ id, ...patch }) => {
      await qc.cancelQueries({ queryKey: ['calendars'] });
      const previous = qc.getQueryData<{
        calendars: CalendarWithRole[];
      }>(['calendars']);
      qc.setQueryData<{ calendars: CalendarWithRole[] }>(
        ['calendars'],
        old => {
          if (!old) return old;
          return {
            calendars: old.calendars.map(c =>
              c.id === id ? { ...c, ...patch } : c
            ),
          };
        }
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['calendars'], ctx.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['calendars'] });
    },
  });
}

export function useDeleteCalendar() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.calendars.delete(id),
    onMutate: async id => {
      await qc.cancelQueries({ queryKey: ['calendars'] });
      const previous = qc.getQueryData<{
        calendars: CalendarWithRole[];
      }>(['calendars']);
      qc.setQueryData<{ calendars: CalendarWithRole[] }>(
        ['calendars'],
        old => {
          if (!old) return old;
          return {
            calendars: old.calendars.filter(
              c => c.id !== id
            ),
          };
        }
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['calendars'], ctx.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['calendars'] });
    },
  });
}

export function useLeaveMembership() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (calendarId: string) =>
      api.calendars.leaveMembership(calendarId),
    onSuccess: (_data, calendarId) => {
      qc.setQueryData<{ calendars: CalendarWithRole[] }>(
        ['calendars'],
        old => {
          if (!old) return old;
          return {
            calendars: old.calendars.filter(
              c => c.id !== calendarId
            ),
          };
        }
      );
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['calendars'] });
    },
  });
}

export { ApiError };
